/**
 * Phase 1 multi-site — site picker landing.
 *
 * Behaviour:
 *   • Hits the public /api/sites-registry/public list.
 *   • If exactly ONE site exists (Salem-only), auto-selects + redirects
 *     to /login silently → ZERO change for current Salem operators.
 *   • If 2+ sites exist, shows a card-grid picker.
 *   • Selection writes localStorage.active_site_id and navigates to /login.
 *
 * Design contract: do NOT block existing Salem login flows. Until the
 * Super Admin creates a 2nd site, every visit auto-skips this page.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Loader2, MapPin, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SiteSelect() {
  const [sites, setSites] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Phase 1: anonymous endpoint, no auth header needed.
        const r = await axios.get(`${API}/api/sites-registry/public`);
        if (!mounted) return;
        const list = r.data?.sites || [];
        setSites(list);
        if (list.length <= 1) {
          // Auto-select the only available site (or fall back to salem).
          const sid = list[0]?.site_id || 'salem';
          localStorage.setItem('active_site_id', sid);
          localStorage.setItem('active_site_name', list[0]?.site_name || 'Salem');
          navigate('/login', { replace: true });
        }
      } catch (e) {
        // Don't block login on a registry-read failure — fall through to Salem.
        console.warn('[site-select] registry fetch failed; defaulting to Salem', e);
        if (!mounted) return;
        localStorage.setItem('active_site_id', 'salem');
        localStorage.setItem('active_site_name', 'Salem');
        setError('Unable to load sites — proceeding to Salem login');
        setTimeout(() => navigate('/login', { replace: true }), 1000);
      }
    })();
    return () => { mounted = false; };
  }, [navigate]);

  const pick = (site) => {
    localStorage.setItem('active_site_id', site.site_id);
    localStorage.setItem('active_site_name', site.site_name);
    navigate('/login');
  };

  if (sites === null) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center" data-testid="site-select-loading">
        <Loader2 className="h-8 w-8 animate-spin text-stone-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center text-sm text-stone-600">
        {error}…
      </div>
    );
  }

  if (sites.length <= 1) {
    return null; // already redirected
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5] flex flex-col items-center justify-center p-6" data-testid="site-select-page">
      <div className="w-full max-w-3xl">
        <div className="text-center mb-8">
          <Building2 className="h-12 w-12 mx-auto text-stone-700 mb-3" />
          <h1 className="text-3xl font-bold text-stone-900">Select Your Site</h1>
          <p className="text-sm text-stone-500 mt-2">
            Choose the site you'll be working in. You'll be taken to the login screen for that site.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {sites.map((s) => (
            <Card
              key={s.site_id}
              onClick={() => pick(s)}
              className="p-5 cursor-pointer hover:bg-stone-50 hover:border-emerald-400 transition-colors"
              data-testid={`site-card-${s.site_id}`}
            >
              <div className="flex items-start gap-3">
                <div className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded text-xs font-mono font-semibold">
                  {s.site_code}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-stone-900 text-base">
                    {s.site_name}
                    {s.is_default && (
                      <span className="ml-2 text-[10px] text-stone-500 font-normal">default</span>
                    )}
                  </div>
                  {s.location && (
                    <div className="text-xs text-stone-500 mt-1 flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {s.location}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
